import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, Upload, FileDown, Search, Pencil } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { money } from "./billingCommon";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
const STATUS_COLOR = {
  Draft: "bg-slate-100 text-slate-600", Sent: "bg-blue-100 text-blue-700", Signed: "bg-indigo-100 text-indigo-700",
  Active: "bg-emerald-100 text-emerald-700", Expired: "bg-amber-100 text-amber-700", Cancelled: "bg-red-100 text-red-700",
};

export default function Contracts() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data } = useQuery({ queryKey: ["contracts", search], queryFn: () => api.get("/contracts", { params: search ? { search } : {} }).then(r => r.data), enabled: ["owner", "admin"].includes(user?.role) });
  const contracts = data?.contracts || []; const statuses = data?.statuses || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["contracts"] }); qc.invalidateQueries({ queryKey: ["client"] }); };

  if (user && !["owner", "admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;

  const setStatus = async (c, status) => { try { await api.put(`/contracts/${c.id}`, { status }); refresh(); toast.success("Updated"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async (c) => { await api.delete(`/contracts/${c.id}`); refresh(); toast.success("Contract deleted"); };

  return (
    <div>
      <PageTitle title="Contracts" subtitle="Upload and track client agreements" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setCreating(true)} data-testid="new-contract-btn"><Plus className="h-4 w-4" /> New Contract</Button>
      } />
      <div className="relative max-w-md mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input className={`${input} pl-9`} placeholder="Search by title or client…" value={search} onChange={e => setSearch(e.target.value)} data-testid="contracts-search" />
      </div>
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="contracts-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono">
            <tr><th className="text-left p-3">Contract</th><th className="text-left p-3">Client</th><th className="text-left p-3">Value</th><th className="text-left p-3">Dates</th><th className="text-left p-3">Status</th><th className="text-left p-3">File</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {contracts.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">No contracts yet.</td></tr> :
              contracts.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                  <td className="p-3"><span className="font-medium text-navy">{c.title}</span><span className="block text-xs text-slate-400 font-mono">{c.contractNumber}</span></td>
                  <td className="p-3 text-slate-600">{c.clientName || c.clientEmail}<span className="block text-xs text-slate-400">{c.clientEmail}</span></td>
                  <td className="p-3 text-navy">{money(c.value)}</td>
                  <td className="p-3 text-xs text-slate-500">{c.startDate || "—"}{c.endDate ? ` → ${c.endDate}` : ""}</td>
                  <td className="p-3"><select value={c.status} onChange={e => setStatus(c, e.target.value)} className={`text-xs rounded-full px-2 py-1 border-0 cursor-pointer ${STATUS_COLOR[c.status] || "bg-slate-100"}`} data-testid={`contract-status-${c.id}`}>{statuses.map(s => <option key={s}>{s}</option>)}</select></td>
                  <td className="p-3">{c.fileName ? <a href={`${API}/contracts/${c.id}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-electric hover:underline" data-testid={`contract-file-${c.id}`}><FileDown className="h-3.5 w-3.5" /> View</a> : <span className="text-xs text-slate-400">—</span>}</td>
                  <td className="p-3"><div className="flex gap-1 justify-end">
                    <button onClick={() => setEditing(c)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-contract-${c.id}`}><Pencil className="h-4 w-4" /></button>
                    <ConfirmDelete onConfirm={() => remove(c)} title="Delete this contract?" description={`${c.title} for ${c.clientName || c.clientEmail} will be removed.`} testid={`contract-delete-${c.id}`} />
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {creating && <ContractDialog statuses={statuses} onClose={() => setCreating(false)} onSaved={() => { refresh(); setCreating(false); }} />}
      {editing && <ContractDialog statuses={statuses} contract={editing} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />}
    </div>
  );
}

function ContractDialog({ contract, statuses, onClose, onSaved }) {
  const isEdit = !!contract;
  const [form, setForm] = useState({
    title: contract?.title || "", clientEmail: contract?.clientEmail || "", clientName: contract?.clientName || "",
    status: contract?.status || "Draft", value: contract?.value || 0, startDate: contract?.startDate || "", endDate: contract?.endDate || "", notes: contract?.notes || "",
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const { data: clientsData } = useQuery({ queryKey: ["clients", ""], queryFn: () => api.get("/clients").then(r => r.data), enabled: !isEdit });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) { await api.put(`/contracts/${contract.id}`, { ...form, value: Number(form.value) }); toast.success("Contract updated"); }
      else { await api.post("/contracts", { ...form, value: Number(form.value) }); toast.success("Contract created"); }
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !isEdit) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/contracts/${contract.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{isEdit ? `${contract.contractNumber}` : "New Contract"}</DialogTitle><DialogDescription>{isEdit ? "Edit contract details or attach a signed file." : "Create a contract, then reopen it to attach a file."}</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="contract-form">
        <input className={input} placeholder="Contract title" required value={form.title} onChange={e => set("title", e.target.value)} data-testid="contract-title" />
        <div className="grid grid-cols-2 gap-3">
          <input className={input} type="email" placeholder="Client email" required value={form.clientEmail} onChange={e => set("clientEmail", e.target.value)} list="client-emails" data-testid="contract-client-email" />
          <input className={input} placeholder="Client name" value={form.clientName} onChange={e => set("clientName", e.target.value)} data-testid="contract-client-name" />
        </div>
        {!isEdit && <datalist id="client-emails">{(clientsData?.clients || []).map(c => <option key={c.email} value={c.email}>{c.name}</option>)}</datalist>}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-slate-600">Value $<input className={input} type="number" step="0.01" value={form.value} onChange={e => set("value", e.target.value)} data-testid="contract-value" /></label>
          <label className="text-sm text-slate-600">Status<select className={input} value={form.status} onChange={e => set("status", e.target.value)} data-testid="contract-status-select">{statuses.map(s => <option key={s}>{s}</option>)}</select></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-slate-600">Start date<input className={input} type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)} /></label>
          <label className="text-sm text-slate-600">End date<input className={input} type="date" value={form.endDate} onChange={e => set("endDate", e.target.value)} /></label>
        </div>
        <textarea className={`${input} h-auto py-2`} rows={2} placeholder="Notes" value={form.notes} onChange={e => set("notes", e.target.value)} />
        {isEdit && (
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-600 mb-2 flex items-center gap-2"><FileText className="h-4 w-4" /> {contract.fileName ? `Attached: ${contract.fileName}` : "No file attached yet"}</p>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx" className="hidden" onChange={uploadFile} data-testid="contract-file-input" />
            <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="contract-upload-btn"><Upload className="h-4 w-4" /> {uploading ? "Uploading…" : contract.fileName ? "Replace file" : "Upload file"}</Button>
          </div>
        )}
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="contract-save">{isEdit ? "Save Changes" : "Create Contract"}</Button>
        {!isEdit && <p className="text-xs text-slate-400 text-center">Create the contract first, then reopen it to attach a signed file.</p>}
      </form>
    </DialogContent></Dialog>
  );
}
